import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { variation } from 'ember-launch-darkly';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import { or, eq, and, not } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import { TrackedObject } from 'tracked-built-ins';
import * as Sentry from '@sentry/ember';
import {
  HdsPageHeader,
  HdsFormRadioCardGroup,
  HdsBadge,
  HdsIcon,
  HdsTextBody,
  HdsFormTextInputField,
  HdsTextDisplay,
  HdsFormSelectField,
  HdsFormCheckboxField,
  HdsLinkStandalone,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { pick, queue } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import style_ from 'ember-style-modifier/modifiers/style';
import {
  ORGANIZATION_CREATED,
  ORGANIZATION_OWNERSHIP_CREATED,
} from 'core/utils/consts/analytics-events/platform';
import {
  CoreLayoutMain,
  LimitBanner,
  FlexGrid,
  Flex,
  WithErrors,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import { resourceTypeFor } from 'core/helpers';
import billingCountriesList from 'billing-common/helpers/billing-countries-list';

export const COUNTRY_CODE_US = 'US';
export const ORG_PURPOSE = {
  BUSINESS: 'business',
  PERSONAL: 'personal',
};
/**
 *
 * `PageOrganizationsCreate` renders the organization create form.
 *
 *
 * ```
 * <Page::Organizations::Create
 *  @onSuccess={{this.model.onSuccess}}
    @organizations={{this.model.organizations}}
    @userId={{this.model.userId}}
 * />
 * ```
 *
 * @class PageOrganizationsCreate
 *
 */

/**
 *
 * `onSuccess` is the async callback function for successfully creating an org.
 * @argument onSuccess;
 * @type {function}
 *
 */

/**
 *
 * `organizations` is a list of organizations a user owns.
 * @argument organizations;
 * @type {Array}
 * @required
 *
 */

/**
 *
 * `userId` is the userId of the user viewing this page.
 * @argument userId;
 * @type {string}
 * @required
 *
 */

export const NAME_ALREADY_EXISTS_ERROR =
  'An organization with this name already exists. Enter a different name.';

export default class PageOrganizationsCreate extends Component {
  @service abilities;
  @service analytics;
  @service api;
  @service billing;
  @service currentUser;
  @service flashMessages;
  @service intl;
  @service('resources.project') projects;
  @service router;
  @service userContext;

  @tracked orgName = this.currentUser?.defaultOrgName;
  @tracked orgPurpose = variation('hcp-ui-flex-2-business-organization')
    ? undefined
    : ORG_PURPOSE.PERSONAL;
  @tracked termsAndConditionsAccepted = false;

  businessInfo = new TrackedObject({
    businessName: undefined,
    addressLine1: undefined,
    addressLine2: undefined, // Not used
    city: undefined,
    state: undefined,
    postalCode: undefined,
    countryCode: COUNTRY_CODE_US,
  });

  ORG_PURPOSE = ORG_PURPOSE;

  toggleTermsAndConditions = () => {
    this.termsAndConditionsAccepted = !this.termsAndConditionsAccepted;
  };

  validate = task(async () => {
    const validation = new Error();
    const fieldViolations = [];

    validation.title = `${this.intl.t(
      'components.page.organizations.create.error.something-went-wrong'
    )}.`;
    validation.message = this.intl.t(
      'components.page.organizations.create.error.fix-the-errors-in-the-form-before-continuing'
    );
    validation.details = [];

    if (!this.orgPurpose) {
      fieldViolations.push({
        field: 'purpose',
        description: this.intl.t(
          'components.page.organizations.create.error.purpose-required'
        ),
      });
    }

    if (!this.orgName?.length) {
      fieldViolations.push({
        field: 'name',
        description: this.intl.t(
          'components.page.organizations.create.error.field-name-is-required',
          {
            fieldName: this.intl.t(
              'components.page.organizations.create.form.name.label'
            ),
          }
        ),
      });
    }

    if (this.orgPurpose === ORG_PURPOSE.BUSINESS) {
      if (!this.termsAndConditionsAccepted) {
        fieldViolations.push({
          field: 'terms_and_conditions',
          description: this.intl.t(
            'components.page.organizations.create.error.terms-and-conditions-accepted'
          ),
        });
      }

      if (!this.businessInfo.businessName) {
        fieldViolations.push({
          field: 'business_name',
          description: this.intl.t(
            'components.page.organizations.create.error.field-name-is-required',
            {
              fieldName: this.intl.t(
                'components.page.organizations.create.form.company.name'
              ),
            }
          ),
        });
      }

      if (!this.businessInfo.addressLine1) {
        fieldViolations.push({
          field: 'address_line1',
          description: this.intl.t(
            'components.page.organizations.create.error.field-name-is-required',
            {
              fieldName: this.intl.t(
                'components.page.organizations.create.form.company.address-line-1'
              ),
            }
          ),
        });
      }

      if (!this.businessInfo.city) {
        fieldViolations.push({
          field: 'city',
          description: this.intl.t(
            'components.page.organizations.create.error.field-name-is-required',
            {
              fieldName: this.intl.t(
                'components.page.organizations.create.form.company.city'
              ),
            }
          ),
        });
      }

      if (!this.businessInfo.countryCode) {
        fieldViolations.push({
          field: 'country_code',
          description: this.intl.t(
            'components.page.organizations.create.error.field-name-is-required',
            {
              fieldName: this.intl.t(
                'components.page.organizations.create.form.company.country'
              ),
            }
          ),
        });
      }
    }

    if (fieldViolations.length) {
      validation.details.push({ field_violations: fieldViolations });
    }

    if (this.submit.performCount > 0 && validation.details.length) {
      throw validation;
    }

    await validation;
  });

  save = task(async () => {
    const { organization = {} } = await this.createOrganization.perform();
    let project;

    try {
      ({ project = {} } = await this.projects.createProject.perform(
        this.currentUser?.defaultProjectName,
        '',
        {
          id: organization.id,
          type: 'ORGANIZATION',
        },
        this.orgPurpose === ORG_PURPOSE.BUSINESS
          ? this.businessInfo.countryCode
          : COUNTRY_CODE_US,
        true
      ));
    } catch (e) {
      const hangingOrganizationError = new Error(
        `Project creation failed for organization ${organization.id} and the organization might be without a project. ${e.message}`
      );
      Sentry.captureException(hangingOrganizationError);
      throw e;
    }

    const { onSuccess } = this.args;

    await this.userContext.setOrganization(organization);
    await this.userContext.setProject(project);

    // Perform this call after the organization has been set in userContext
    // so that we have permissions set in permissions service.
    if (this.abilities.can('create ownership in organization')) {
      const organizationOwnership =
        this.orgPurpose === ORG_PURPOSE.BUSINESS
          ? {
              business: {
                ...(this.businessInfo ?? {}),
              },
            }
          : {
              personal: {},
            };
      await this.createOrgOwnership.perform({
        organization,
        organizationOwnership,
      });
    }

    this.flashMessages.success(
      this.intl.t('components.page.organizations.create.flash.success')
    );

    this.currentUser.organizations = [
      ...this.currentUser.organizations,
      organization,
    ];

    if (onSuccess && typeof onSuccess === 'function') {
      await onSuccess({ organization, project });
    }
  });

  submit = task(async (event) => {
    event.preventDefault();

    await this.validate.perform();
    await this.save.perform();
  });

  createOrganization = task(async () => {
    const reqBody = { name: this.orgName.trim() };

    let resp;
    try {
      resp =
        await this.api.resourceManager.org.organizationServiceCreate(reqBody);
    } catch (e) {
      resp = await e.json();

      switch (resp.message) {
        case NAME_ALREADY_EXISTS_ERROR: {
          resp.title = `${this.intl.t(
            'components.page.organizations.create.error.organization-name-must-be-unique'
          )}.`;
          resp.message = this.intl.t(
            'components.page.organizations.create.error.the-organization-name-is-already-in-use'
          );
          resp.details = [
            {
              field_violations: [
                {
                  field: 'name',
                  description: this.intl.t(
                    'components.page.organizations.create.error.organization-name-must-be-unique'
                  ),
                },
              ],
            },
          ];
          break;
        }
      }

      const modifiedResponse = new Response(JSON.stringify(resp), {
        status: e.status,
        statusText: e.statusText,
        headers: e.headers,
      });

      throw modifiedResponse;
    }

    this.analytics.trackEvent(ORGANIZATION_CREATED, {
      payload: reqBody,
      resp,
    });

    return resp;
  });

  createOrgOwnership = task(
    async ({ organization, organizationOwnership = {} }) => {
      let resp;
      const reqBody = {
        organizationOwnership,
      };

      try {
        resp =
          await this.api.billing.organizationOwnership.organizationOwnershipServiceCreate(
            organization?.id,
            reqBody
          );
      } catch (e) {
        if (macroCondition(isDevelopingApp())) {
          console.error(e);
        }
      }

      this.analytics.trackEvent(ORGANIZATION_OWNERSHIP_CREATED, {
        payload: reqBody,
        resp,
      });

      return resp;
    }
  );

  <template>
    {{#let
      (can "create organizations" @organizations userId=@userId)
      as |canCreate|
    }}
      <WithErrors
        @error={{or this.validate.last.error this.save.last.error}}
        as |FormError FieldError|
      >
        <CoreLayoutMain data-test-org-create>
          <:header>
            <HdsPageHeader as |PH|>
              <PH.Title>
                {{t "components.page.organizations.create.title"}}
              </PH.Title>
              <PH.Subtitle>
                {{t "components.page.organizations.create.subtitle"}}
              </PH.Subtitle>
            </HdsPageHeader>
          </:header>
          <:alert>
            <LimitBanner
              @count={{this.currentUser.ownedOrganizations.length}}
              @variant={{resourceTypeFor "organization"}}
              @padding="0"
            />
            {{#if this.save.last.error}}
              <FormError @shouldRenderPageError={{true}} />
            {{/if}}
          </:alert>
          <:body>

            <FlexGrid as |Grid|>
              {{#if (variation "hcp-ui-flex-2-business-organization")}}
                <Grid.Item
                  @xs="12"
                  @sm="12"
                  @md={{if @organizations.length "8" "12"}}
                  @lg={{if @organizations.length "8" "12"}}
                >
                  <FieldError
                    @field="purpose"
                    @hdsDelegatedError={{true}}
                    as |err|
                  >
                    <HdsFormRadioCardGroup
                      class="organizations-create-business-purpose-cards-container"
                      {{boxPadding "0 0 lg 0"}}
                      as |G|
                    >
                      <G.Legend>
                        {{t
                          "components.page.organizations.create.select-org-type"
                        }}
                        <HdsBadge
                          @text={{t
                            "components.page.organizations.create.not-editable-after-creation"
                          }}
                          @color="highlight"
                        />
                      </G.Legend>
                      <G.RadioCard
                        @checked={{eq
                          this.orgPurpose
                          this.ORG_PURPOSE.BUSINESS
                        }}
                        @value={{this.ORG_PURPOSE.BUSINESS}}
                        {{on
                          "change"
                          (pick "target.value" (set this "orgPurpose"))
                        }}
                        data-test-org-create-business-radio-card
                        as |R|
                      >
                        <R.Icon @name="briefcase" />
                        <R.Label>
                          {{t
                            "components.page.organizations.create.business-card.label"
                          }}
                        </R.Label>
                        <R.Generic>
                          <ul class="unstyled hds-foreground-primary">
                            <li {{boxPadding "0 0 xs 0"}}>
                              <Flex
                                @alignItems="center"
                                @direction="row"
                                @gap="xs"
                              >
                                <HdsIcon @name="check-circle" @size="16" />
                                <HdsTextBody>
                                  {{t
                                    "components.page.organizations.create.business-card.list-item-1"
                                  }}
                                </HdsTextBody>
                              </Flex>
                            </li>

                            <li {{boxPadding "0 0 xs 0"}}>
                              <Flex
                                @alignItems="center"
                                @direction="row"
                                @gap="xs"
                              >
                                <HdsIcon @name="check-circle" @size="16" />
                                <HdsTextBody>
                                  {{t
                                    "components.page.organizations.create.business-card.list-item-2"
                                  }}
                                </HdsTextBody>
                              </Flex>
                            </li>

                            <li {{boxPadding "0 0 xs 0"}}>
                              <Flex
                                @alignItems="center"
                                @direction="row"
                                @gap="xs"
                              >
                                <HdsIcon @name="check-circle" @size="16" />
                                <HdsTextBody>
                                  {{t
                                    "components.page.organizations.create.business-card.list-item-3"
                                  }}
                                </HdsTextBody>
                              </Flex>
                            </li>
                          </ul>
                        </R.Generic>
                      </G.RadioCard>
                      <G.RadioCard
                        @checked={{eq
                          this.orgPurpose
                          this.ORG_PURPOSE.PERSONAL
                        }}
                        @value={{this.ORG_PURPOSE.PERSONAL}}
                        {{on
                          "change"
                          (pick "target.value" (set this "orgPurpose"))
                        }}
                        data-test-org-create-personal-radio-card
                        as |R|
                      >
                        <R.Icon @name="user" />
                        <R.Label>
                          {{t
                            "components.page.organizations.create.personal.label"
                          }}
                        </R.Label>
                        <R.Generic>
                          <ul class="unstyled hds-foreground-primary">
                            <li>
                              <Flex
                                @alignItems="center"
                                @direction="row"
                                @gap="xs"
                              >
                                <HdsIcon @name="check-circle" @size="16" />
                                <HdsTextBody>
                                  {{t
                                    "components.page.organizations.create.personal.list-item-1"
                                  }}
                                </HdsTextBody>
                              </Flex>
                            </li>

                          </ul>
                        </R.Generic>
                      </G.RadioCard>
                      {{#if (and err (not this.orgPurpose))}}
                        <G.Error id="error-purpose">
                          {{err.description}}
                        </G.Error>
                      {{/if}}
                    </HdsFormRadioCardGroup>
                  </FieldError>
                </Grid.Item>
              {{/if}}
              <Grid.Item
                @xs="12"
                @sm="12"
                @md={{if @organizations.length "8" "12"}}
                @lg={{if @organizations.length "8" "12"}}
              >
                <form class="hcp-hds-form" {{on "submit" this.submit.perform}}>
                  <Flex @direction="column" @gap="lg">
                    {{#if this.orgPurpose}}
                      <FieldError
                        @field="name"
                        @hdsDelegatedError={{true}}
                        as |err|
                      >
                        <HdsFormTextInputField
                          @id="org-name"
                          @value={{this.orgName}}
                          disabled={{not canCreate}}
                          {{on
                            "input"
                            (queue
                              (pick "target.value" (set this "orgName"))
                              this.validate.perform
                            )
                          }}
                          data-test-org-create-name-field
                          as |F|
                        >
                          <F.Label>
                            {{t
                              "components.page.organizations.create.form.name.label"
                            }}
                          </F.Label>
                          <F.HelperText>
                            <ul class="input-requirements">
                              <li>
                                {{t
                                  "components.page.organizations.create.form.name.description.must-be-unique"
                                }}
                              </li>
                              <li>
                                {{t
                                  "components.page.organizations.create.form.name.description.may-contain"
                                }}
                              </li>
                            </ul>
                          </F.HelperText>
                          <F.CharacterCount
                            @minLength={{3}}
                            @maxLength={{40}}
                          />
                          {{#if err}}
                            <F.Error>
                              {{err.description}}
                            </F.Error>
                          {{/if}}
                        </HdsFormTextInputField>
                      </FieldError>

                      {{#if (eq this.orgPurpose this.ORG_PURPOSE.BUSINESS)}}
                        <HdsTextDisplay @size="300" @weight="bold">
                          {{t
                            "components.page.organizations.create.form.business-information"
                          }}
                        </HdsTextDisplay>

                        <FieldError
                          @field="business_name"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @id="business-name"
                            @value={{this.businessInfo.businessName}}
                            disabled={{not canCreate}}
                            {{on
                              "input"
                              (queue
                                (pick
                                  "target.value"
                                  (set this.businessInfo "businessName")
                                )
                                this.validate.perform
                              )
                            }}
                            data-test-org-create-business-name-field
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.organizations.create.form.company.name"
                              }}
                            </F.Label>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>

                        <FieldError
                          @field="address_line1"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @id="business-address-line-1"
                            @value={{this.businessInfo.addressLine1}}
                            disabled={{not canCreate}}
                            {{on
                              "input"
                              (queue
                                (pick
                                  "target.value"
                                  (set this.businessInfo "addressLine1")
                                )
                                this.validate.perform
                              )
                            }}
                            data-test-org-create-business-address-line-1-field
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.organizations.create.form.company.address-line-1"
                              }}
                            </F.Label>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>

                        <FieldError
                          @field="address_line2"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @id="business-address-line-2"
                            @value={{this.businessInfo.addressLine2}}
                            disabled={{not canCreate}}
                            {{on
                              "input"
                              (queue
                                (pick
                                  "target.value"
                                  (set this.businessInfo "addressLine2")
                                )
                                this.validate.perform
                              )
                            }}
                            data-test-org-create-business-address-line-2-field
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.organizations.create.form.company.address-line-2"
                              }}
                            </F.Label>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>

                        <FieldError
                          @field="city"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @id="business-city"
                            @value={{this.businessInfo.city}}
                            disabled={{not canCreate}}
                            {{on
                              "input"
                              (queue
                                (pick
                                  "target.value" (set this.businessInfo "city")
                                )
                                this.validate.perform
                              )
                            }}
                            data-test-org-create-business-city-field
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.organizations.create.form.company.city"
                              }}
                            </F.Label>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>

                        <Flex @direction="row" @gap="md">
                          <FieldError
                            @field="state"
                            @hdsDelegatedError={{true}}
                            as |err|
                          >
                            <HdsFormTextInputField
                              @id="business-state"
                              @value={{this.businessInfo.state}}
                              disabled={{not canCreate}}
                              {{on
                                "input"
                                (queue
                                  (pick
                                    "target.value"
                                    (set this.businessInfo "state")
                                  )
                                  this.validate.perform
                                )
                              }}
                              data-test-org-create-business-state-field
                              as |F|
                            >
                              <F.Label>
                                {{t
                                  "components.page.organizations.create.form.company.state-providence"
                                }}
                              </F.Label>
                              {{#if err}}
                                <F.Error>
                                  {{err.description}}
                                </F.Error>
                              {{/if}}
                            </HdsFormTextInputField>
                          </FieldError>
                          <FieldError
                            @field="postal_code"
                            @hdsDelegatedError={{true}}
                            as |err|
                          >
                            <HdsFormTextInputField
                              @id="business-postal-code"
                              @value={{this.businessInfo.postalCode}}
                              disabled={{not canCreate}}
                              {{on
                                "input"
                                (queue
                                  (pick
                                    "target.value"
                                    (set this.businessInfo "postalCode")
                                  )
                                  this.validate.perform
                                )
                              }}
                              data-test-org-create-business-postal-code-field
                              as |F|
                            >
                              <F.Label>
                                {{t
                                  "components.page.organizations.create.form.company.postal-code"
                                }}
                              </F.Label>
                              {{#if err}}
                                <F.Error>
                                  {{err.description}}
                                </F.Error>
                              {{/if}}
                            </HdsFormTextInputField>
                          </FieldError>
                        </Flex>

                        <FieldError
                          @field="country_code"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormSelectField
                            @id="business-country-code"
                            disabled={{not canCreate}}
                            {{on
                              "input"
                              (queue
                                (pick
                                  "target.value"
                                  (set this.businessInfo "countryCode")
                                )
                                this.validate.perform
                              )
                            }}
                            data-test-org-create-business-country-code-field
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.organizations.create.form.company.country"
                              }}
                            </F.Label>
                            <F.Options>
                              {{#each-in
                                (billingCountriesList)
                                as |code country|
                              }}
                                <option
                                  value={{code}}
                                  selected={{eq
                                    this.businessInfo.countryCode
                                    code
                                  }}
                                >
                                  {{country}}
                                </option>
                              {{/each-in}}
                            </F.Options>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormSelectField>
                        </FieldError>

                        <FieldError
                          @field="terms_and_conditions"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormCheckboxField
                            @id="business-terms-and-conditions"
                            {{on
                              "change"
                              (queue
                                this.toggleTermsAndConditions
                                this.validate.perform
                              )
                            }}
                            data-test-org-create-business-terms-and-conditions
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.organizations.create.form.terms-and-condition.label"
                              }}
                            </F.Label>
                            <F.HelperText>
                              {{t
                                "components.page.organizations.create.form.terms-and-condition.helper-text"
                              }}
                              <br />
                              <HdsLinkStandalone
                                @icon="external-link"
                                @iconPosition="trailing"
                                @text={{t
                                  "components.page.organizations.create.form.terms-and-condition.link-text"
                                }}
                                @href="https://eula.hashicorp.com/OnlineAgreements.pdf"
                              />
                            </F.HelperText>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormCheckboxField>
                        </FieldError>
                      {{/if}}
                    {{/if}}
                    <footer>
                      <HdsButtonSet>
                        {{#if canCreate}}
                          <HdsButton
                            {{style_ width="193px"}}
                            @icon={{if this.save.isRunning "loading"}}
                            @text={{if
                              this.save.isRunning
                              (t
                                "components.page.organizations.create.in-progress"
                              )
                              (t "components.page.organizations.create.action")
                            }}
                            disabled={{this.save.isRunning}}
                            type="submit"
                            data-test-org-create-submit
                          />
                        {{/if}}
                        {{#if @organizations.length}}
                          <HdsButton
                            @color="secondary"
                            @route="cloud.orgs.list"
                            @text={{t "components.form.cancel"}}
                            data-test-org-create-cancel
                          />
                        {{/if}}
                      </HdsButtonSet>
                    </footer>
                  </Flex>
                </form>
              </Grid.Item>
            </FlexGrid>
          </:body>
        </CoreLayoutMain>
      </WithErrors>
    {{/let}}
  </template>
}
